import React from 'react';
import {HubConnectionBuilder} from '@microsoft/signalr';
import {useAuthStateManager} from '../../hooks/useAuthStateManager.tsx';
import useClientConfig from '../../hooks/useClientConfig.tsx';
import {useEffectOnce} from '../../hooks/useEffectOnce.ts';
import {PostUpdateMessage} from '../../types.ts';
import joinPath from '../../lib/joinPath.ts';
import hubReconnectPolicy from '../../lib/hubReconnectPolicy.ts';

interface UserMessagesHubProps {
    apiServiceBaseUri: string;
    getAccessToken: () => Promise<string>;
}

/*
 * The messages hub requires a bearer token and derives the user's group from
 * it, so the connection sends the token and JoinGroup takes no argument.
 */
const UserMessagesHub: React.FC<
    React.PropsWithChildren<UserMessagesHubProps>
> = ({children, apiServiceBaseUri, getAccessToken}) => {
    useEffectOnce(() => {
        const hubUrl = joinPath(apiServiceBaseUri, 'messagesHub');

        const hubConnection = new HubConnectionBuilder()
            .withUrl(hubUrl, {accessTokenFactory: () => getAccessToken()})
            .withAutomaticReconnect(hubReconnectPolicy)
            .build();

        hubConnection.on('PostsUpdated', onPostsUpdated);
        hubConnection.on('NewNotifications', onNewNotifications);

        // A reconnect is a new connection, outside the user's group; without this, notifications stop until a reload.
        hubConnection.onreconnected(() => {
            hubConnection.invoke('JoinGroup').catch(() => undefined);
        });

        hubConnection.start().then(() => {
            hubConnection.invoke('JoinGroup');
        });

        return () => {
            if (hubConnection) {
                hubConnection.invoke('LeaveGroup').catch(() => undefined);
                hubConnection.stop();
            }
        };
    });

    const onPostsUpdated = (message: PostUpdateMessage) => {
        window.dispatchEvent(
            new CustomEvent('PostsUpdated', {detail: message})
        );
    };

    const onNewNotifications = () => {
        window.dispatchEvent(new CustomEvent('NewNotifications'));
    };

    return <>{children}</>;
};

export const MxMessagesHub: React.FC<React.PropsWithChildren> = ({
    children
}) => {
    const {apiServiceBaseUri} = useClientConfig();
    const {authData, getAccessToken} = useAuthStateManager();

    if (authData.isAuthenticated && authData.id && apiServiceBaseUri !== '') {
        return (
            <UserMessagesHub
                getAccessToken={getAccessToken}
                apiServiceBaseUri={apiServiceBaseUri}
                key={`${authData.id}_${apiServiceBaseUri}`}
            >
                {children}
            </UserMessagesHub>
        );
    }

    return <>{children}</>;
};
